/**
 * The fight simulation (docs/fight.md). Pure and deterministic: the same setup and inputs give
 * the same states on every engine. Advance with step() at FIGHT.tickRate.
 */
import { FIGHT } from '../config/fight';
import { PERKS } from '../config/skills';
import { BUTTON, NO_INPUT, quantizeInput, type FighterInput } from './input';
import { OCTAGON_NORMALS, expNeg, length } from './math';
import { moveCost, oneTwoTiming, signatureTiming, strikeTiming } from './mods';
import type {
  Action,
  ActionType,
  FightEvent,
  FightResult,
  FightSetup,
  FightState,
  FighterMods,
  FighterState,
  MoveType,
  Phase,
  Side,
  SkillDamage,
} from './types';

export const TICK_SECONDS = 1 / FIGHT.tickRate;
export const RADIUS = FIGHT.size * FIGHT.radiusFactor;

/** Strike reach for a fighter (2R + 0.42 S, times the Reach skill). */
export function reachOf(mods: FighterMods): number {
  return (RADIUS * 2 + FIGHT.size * FIGHT.strike.reachExtra) * mods.reachMul;
}

// Event payloads without the id/tick added by emit(). Distributive so each variant keeps its fields.
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
type EventInput = DistributiveOmit<FightEvent, 'id' | 'tick'>;
type Emit = (e: EventInput) => void;

// ---------------------------------------------------------------------------------------------
// Creation

export function createFight(setup: FightSetup): FightState {
  const fighters: [FighterState, FighterState] = [newFighter(0, setup), newFighter(1, setup)];
  const s: FightState = {
    setup,
    tick: 0,
    phase: 'intro',
    phaseT: 0,
    round: 1,
    clock: FIGHT.roundSeconds,
    timeScale: 1,
    rng: setup.seed | 0,
    fighters,
    roundWinners: [],
    result: null,
  };
  resetPositions(s);
  return s;
}

function newFighter(side: Side, setup: FightSetup): FighterState {
  const mods = setup.fighters[side].mods;
  const maxHp = Math.round((setup.fighters[side].baseHealth ?? FIGHT.maxHealth) * mods.maxHealthMul);
  return {
    side,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    kx: 0,
    ky: 0,
    face: side === 0 ? 1 : -1,
    hp: maxHp,
    maxHp,
    stamina: FIGHT.staminaMax,
    meter: mods.perks.headStart ? PERKS.headStart.meter : 0,
    act: null,
    actSeq: 0,
    queued: null,
    queuedT: 0,
    blocking: false,
    blockHeld: 0,
    prevButtons: 0,
    hitFlash: 0,
    squash: 0,
    ko: false,
    koT: 0,
    grappleImmune: 0,
    oneTwoWindow: 0,
    ironChinUsed: false,
    rocked: 0,
    rockedThisRound: false,
    recovered: 0,
    punishWindow: 0,
    grabRead: false,
    damageDealt: new Array<number>(FIGHT.rounds).fill(0),
    powerDamage: 0,
    takedownDamage: 0,
  };
}

function resetPositions(s: FightState): void {
  const [cx, cy] = s.setup.arena.center;
  for (const f of s.fighters) {
    f.x = cx + (f.side === 0 ? -FIGHT.startOffsetX : FIGHT.startOffsetX);
    f.y = cy + FIGHT.startOffsetY;
    f.vx = f.vy = f.kx = f.ky = 0;
    f.face = f.side === 0 ? 1 : -1;
  }
}

// ---------------------------------------------------------------------------------------------
// Step

/**
 * Advances the fight by one tick. Mutates `s` in place and appends events to `events`
 * (also returned). Inputs are quantized here, so every client simulates identical values.
 */
export function step(
  s: FightState,
  rawInputs: readonly [FighterInput, FighterInput],
  events: FightEvent[] = [],
): FightEvent[] {
  s.tick++;
  const emit: Emit = (e) => {
    const side = 'side' in e ? e.side : 'attacker' in e ? e.attacker : 'loser' in e ? e.loser : 'striker' in e ? e.striker : '';
    events.push({ ...e, tick: s.tick, id: `${s.tick}:${e.type}:${side}` } as FightEvent);
  };
  const inputs: [FighterInput, FighterInput] = [quantizeInput(rawInputs[0]), quantizeInput(rawInputs[1])];
  const entering = s.phaseT === 0;
  s.phaseT += TICK_SECONDS;

  switch (s.phase) {
    case 'intro':
      if (entering) emit({ type: 'roundIntro', round: s.round });
      settle(s, inputs);
      if (s.phaseT >= FIGHT.introSeconds - 1e-9) {
        enterPhase(s, 'fight');
        emit({ type: 'fight', round: s.round });
      }
      break;

    case 'fight':
      simulate(s, inputs, emit, true);
      if (s.phase === 'fight') {
        s.clock -= TICK_SECONDS * s.timeScale;
        if (s.clock <= 0) {
          s.clock = 0;
          endRoundOnTime(s, emit);
        }
      }
      break;

    case 'ko':
      simulate(s, inputs, emit, false);
      if (s.phaseT >= FIGHT.koSeconds - 1e-9) finish(s, emit);
      break;

    case 'timeUp':
      settle(s, inputs);
      if (s.phaseT >= FIGHT.timeUpSeconds - 1e-9) {
        if (s.round < FIGHT.rounds) {
          recoverForBreak(s);
          enterPhase(s, 'break');
        } else decide(s);
      }
      break;

    case 'break':
      settle(s, inputs);
      if (s.phaseT >= FIGHT.breakSeconds - 1e-9) startNextRound(s);
      break;

    case 'decision':
      settle(s, inputs);
      if (s.phaseT >= FIGHT.decisionSeconds - 1e-9) finish(s, emit);
      break;

    case 'over':
      break;
  }

  s.timeScale = Math.min(1, s.timeScale + FIGHT.slowMo.recovery * TICK_SECONDS);
  return events;
}

function enterPhase(s: FightState, phase: Phase): void {
  s.phase = phase;
  s.phaseT = 0;
}

// ---------------------------------------------------------------------------------------------
// Round flow

function endRoundOnTime(s: FightState, emit: Emit): void {
  const r = s.round - 1;
  const d0 = s.fighters[0].damageDealt[r] ?? 0;
  const d1 = s.fighters[1].damageDealt[r] ?? 0;
  const winner: Side | null = d0 > d1 ? 0 : d1 > d0 ? 1 : null;
  s.roundWinners[r] = winner;
  for (const f of s.fighters) {
    f.act = null;
    f.queued = null;
    f.blocking = false;
    f.rocked = 0;
    f.punishWindow = 0;
    f.grabRead = false;
  }
  emit({ type: 'roundEnd', round: s.round, winner, damage: [d0, d1] });
  enterPhase(s, 'timeUp');
}

/**
 * As a break between rounds starts, each fighter gets a little health back (docs/fight.md §1),
 * so the HUD and the break card already show it, with how much (`recovered`).
 */
function recoverForBreak(s: FightState): void {
  for (const f of s.fighters) {
    const mods = modsOf(s, f.side);
    const heal =
      FIGHT.healthRecoveryBetweenRounds +
      (mods.perks.secondBreath ? PERKS.secondBreath.healthBetweenRounds : 0);
    const gain = Math.max(0, Math.min(f.maxHp - f.hp, heal));
    f.hp += gain;
    f.recovered = gain;
  }
}

function startNextRound(s: FightState): void {
  s.round++;
  s.clock = FIGHT.roundSeconds;
  resetPositions(s);
  for (const f of s.fighters) {
    f.recovered = 0;
    f.stamina = FIGHT.staminaMax;
    f.act = null;
    f.queued = null;
    f.blocking = false;
    f.blockHeld = 0;
    f.hitFlash = 0;
    f.squash = 0;
    f.grappleImmune = 0;
    f.oneTwoWindow = 0;
    f.rocked = 0;
    f.rockedThisRound = false;
    f.punishWindow = 0;
    f.grabRead = false;
  }
  enterPhase(s, 'intro');
}

function decide(s: FightState): void {
  let won0 = 0;
  let won1 = 0;
  for (const w of s.roundWinners) {
    if (w === 0) won0++;
    else if (w === 1) won1++;
  }
  const damage = totalDamage(s);
  let winner: Side | null = null;
  if (won0 !== won1) winner = won0 > won1 ? 0 : 1;
  else if (damage[0] !== damage[1]) winner = damage[0] > damage[1] ? 0 : 1;
  s.result = {
    winner,
    method: winner === null ? 'draw' : 'decision',
    round: s.round,
    roundTime: FIGHT.roundSeconds,
    roundWinners: [...s.roundWinners],
    damage,
    skillDamage: skillDamageOf(s),
  };
  enterPhase(s, 'decision');
}

function finish(s: FightState, emit: Emit): void {
  enterPhase(s, 'over');
  if (s.result) emit({ type: 'fightEnd', result: s.result });
}

function totalDamage(s: FightState): [number, number] {
  const sum = (f: FighterState) => f.damageDealt.reduce((a, b) => a + b, 0);
  return [sum(s.fighters[0]), sum(s.fighters[1])];
}

/** Each side's extra damage from its skills so far (D48), for a fight result. */
export function skillDamageOf(s: FightState): [SkillDamage, SkillDamage] {
  const of = (f: FighterState): SkillDamage => ({ power: f.powerDamage, takedown: f.takedownDamage });
  return [of(s.fighters[0]), of(s.fighters[1])];
}

// ---------------------------------------------------------------------------------------------
// The fight itself

interface Snapshot {
  x: number;
  y: number;
  actType: ActionType | null;
  actT: number;
  blocking: boolean;
  blockHeld: number;
  hittable: boolean;
  grappleable: boolean;
}

type Pending =
  | { kind: 'strike' | 'strike2' | 'signature'; attacker: Side }
  | { kind: 'dodge'; attacker: Side }
  | { kind: 'grapple'; attacker: Side }
  | { kind: 'slam'; attacker: Side };

function modsOf(s: FightState, side: Side): FighterMods {
  return s.setup.fighters[side].mods;
}

function simulate(s: FightState, inputs: [FighterInput, FighterInput], emit: Emit, allowInput: boolean): void {
  const dt = TICK_SECONDS * s.timeScale;
  const [a, b] = s.fighters;

  for (const f of s.fighters) {
    f.hitFlash = Math.max(0, f.hitFlash - dt * 5);
    f.squash = Math.max(0, f.squash - dt * 5);
    const kd = expNeg(FIGHT.knockbackDecay * dt);
    f.kx *= kd;
    f.ky *= kd;
    if (f.ko) {
      f.koT += dt;
      f.vx *= kd;
      f.vy *= kd;
    }
    if (f.grappleImmune > 0) f.grappleImmune = Math.max(0, f.grappleImmune - dt);
    if (f.oneTwoWindow > 0) f.oneTwoWindow = Math.max(0, f.oneTwoWindow - dt);
    // Time on the ground (or caught in a grab) doesn't count: a takedown doesn't waste the finish.
    const grounded = f.act?.type === 'down' || f.act?.type === 'grabbed';
    if (f.rocked > 0 && !grounded) f.rocked = Math.max(0, f.rocked - dt);
    if (f.punishWindow > 0) f.punishWindow = Math.max(0, f.punishWindow - dt);
  }

  if (allowInput) {
    handleInput(s, a, inputs[0], dt, emit);
    handleInput(s, b, inputs[1], dt, emit);
  } else {
    a.blocking = b.blocking = false;
  }

  // Interactions are resolved from the same start-of-step state for both fighters.
  const snaps: [Snapshot, Snapshot] = [snapshot(a), snapshot(b)];
  const pending: Pending[] = [];
  const dashing: [boolean, boolean] = [false, false];
  /** Which fighters took a clean (unblocked) hit this step: they can be rocked. */
  const clean: [boolean, boolean] = [false, false];
  advanceAction(s, a, snaps[0], snaps[1], dt, pending, dashing);
  advanceAction(s, b, snaps[1], snaps[0], dt, pending, dashing);
  // Hits first, then grabs, then slams, so the outcome never depends on which side is 0 or 1.
  for (const p of pending) if (p.kind !== 'grapple' && p.kind !== 'slam') resolve(s, p, snaps, emit, clean);
  for (const p of pending) if (p.kind === 'grapple') resolve(s, p, snaps, emit, clean);
  for (const p of pending) if (p.kind === 'slam') resolve(s, p, snaps, emit, clean);
  checkKnockouts(s, emit);
  checkRocked(s, emit, clean);

  for (const f of s.fighters) move(s, f, allowInput && !f.ko ? inputs[f.side] : NO_INPUT, dt, dashing[f.side]);
  separate(s);
  constrain(s, a);
  constrain(s, b);

  for (const f of s.fighters) {
    if (f.ko) continue;
    const o = s.fighters[f.side === 0 ? 1 : 0];
    const dx = o.x - f.x;
    if (Math.abs(dx) > FIGHT.facingDeadzone && !isLocked(f.act)) f.face = dx > 0 ? 1 : -1;
    const mods = modsOf(s, f.side);
    if (!f.act) {
      const regen = f.blocking ? FIGHT.staminaRegenBlocking : FIGHT.staminaRegen;
      f.stamina = Math.min(FIGHT.staminaMax, f.stamina + regen * mods.staminaRegenMul * dt);
    }
    f.meter = Math.min(FIGHT.meterMax, f.meter + FIGHT.meterPassive * mods.meterGainMul * dt);
  }
}

/** Between rounds and during pauses: no input, motion decays, nothing lands. */
function settle(s: FightState, inputs: [FighterInput, FighterInput]): void {
  const dt = TICK_SECONDS;
  for (const f of s.fighters) {
    f.prevButtons = inputs[f.side].buttons;
    f.blocking = false;
    f.hitFlash = Math.max(0, f.hitFlash - dt * 5);
    f.squash = Math.max(0, f.squash - dt * 5);
    const kd = expNeg(FIGHT.knockbackDecay * dt);
    f.kx *= kd;
    f.ky *= kd;
    const k = Math.min(1, dt * FIGHT.accel);
    f.vx -= f.vx * k;
    f.vy -= f.vy * k;
    if (f.ko) f.koT += dt;
    f.x += (f.vx + f.kx) * dt;
    f.y += (f.vy + f.ky) * dt;
    constrain(s, f);
  }
}

function isLocked(act: Action | null): boolean {
  return (
    act !== null &&
    (act.type === 'stun' || act.type === 'down' || act.type === 'grab' || act.type === 'grabbed')
  );
}

function startAct(f: FighterState, type: ActionType, dur: number): void {
  f.act = { type, t: 0, dur, done: false };
  f.actSeq++;
  f.blocking = false;
  f.blockHeld = 0;
}

/** Stun for `dur`, unless a longer stun is already running. */
function setStun(f: FighterState, dur: number): void {
  if (f.act?.type === 'stun' && f.act.dur - f.act.t >= dur) return;
  startAct(f, 'stun', dur);
}

function handleInput(s: FightState, f: FighterState, input: FighterInput, dt: number, emit: Emit): void {
  const pressed = input.buttons & ~f.prevButtons;
  f.prevButtons = input.buttons;
  if (f.ko) {
    f.blocking = false;
    return;
  }
  const mods = modsOf(s, f.side);
  let consumed = 0;

  // Feint (Speed 5): Defend early in a strike cancels it, before it lands.
  if (
    mods.perks.feint &&
    f.act?.type === 'strike' &&
    !f.act.done &&
    f.act.t < PERKS.feint.window &&
    pressed & BUTTON.defend
  ) {
    f.act = null;
    f.stamina = Math.min(FIGHT.staminaMax, f.stamina + PERKS.feint.staminaRefund);
    emit({ type: 'feint', side: f.side });
  }

  // Reversal (Escape 10): Grapple late in the down time gets up at once and shoves the grappler.
  if (
    mods.perks.reversal &&
    f.act?.type === 'down' &&
    f.act.dur - f.act.t <= PERKS.reversal.window &&
    pressed & BUTTON.grapple
  ) {
    endDown(s, f);
    const o = s.fighters[f.side === 0 ? 1 : 0];
    const dx = o.x - f.x;
    const dy = o.y - f.y;
    const d = length(dx, dy) || 1;
    o.kx += (dx / d) * PERKS.reversal.pushKnockback;
    o.ky += (dy / d) * PERKS.reversal.pushKnockback;
    consumed |= BUTTON.grapple;
    emit({ type: 'reversal', side: f.side });
  }

  if (f.queued) {
    f.queuedT -= dt;
    if (f.queuedT <= 0) f.queued = null;
  }

  const fresh = pressed & ~consumed;
  const move: MoveType | null =
    fresh & BUTTON.signature
      ? 'signature'
      : fresh & BUTTON.grapple
        ? 'grapple'
        : fresh & BUTTON.strike
          ? 'strike'
          : null;
  if (move) {
    if (f.act) {
      f.queued = move;
      f.queuedT = FIGHT.inputBuffer;
    } else {
      tryMove(s, f, move, emit);
    }
  } else if (!f.act && f.queued) {
    const q = f.queued;
    f.queued = null;
    tryMove(s, f, q, emit);
  }

  const defendHeld = (input.buttons & BUTTON.defend) !== 0;
  f.blocking = defendHeld && !f.act;
  f.blockHeld = f.blocking ? f.blockHeld + dt : 0;
}

function tryMove(s: FightState, f: FighterState, move: MoveType, emit: Emit): void {
  const mods = modsOf(s, f.side);
  if (f.rocked > 0) {
    emit({ type: 'dazed', side: f.side, move });
    return;
  }
  if (move === 'signature') {
    if (f.meter < FIGHT.meterMax) return;
    f.meter = 0;
    startAct(f, 'signature', signatureTiming(mods).duration);
    emit({ type: 'moveStart', side: f.side, move });
    return;
  }
  if (move === 'strike' && mods.perks.oneTwo && f.oneTwoWindow > 0) {
    const cost = moveCost(mods, 'strike2');
    if (f.stamina < cost) {
      emit({ type: 'tired', side: f.side, move });
      return;
    }
    f.stamina -= cost;
    f.oneTwoWindow = 0;
    startAct(f, 'strike2', oneTwoTiming(mods).duration);
    emit({ type: 'moveStart', side: f.side, move: 'strike2' });
    return;
  }
  const cost = moveCost(mods, move);
  if (f.stamina < cost) {
    emit({ type: 'tired', side: f.side, move });
    return;
  }
  f.stamina -= cost;
  startAct(f, move, move === 'strike' ? strikeTiming(mods).duration : FIGHT.grapple.duration);
  emit({ type: 'moveStart', side: f.side, move });
}

function endDown(s: FightState, f: FighterState): void {
  f.act = null;
  if (modsOf(s, f.side).perks.standUp) f.grappleImmune = PERKS.standUp.grappleImmunity;
}

function snapshot(f: FighterState): Snapshot {
  const t = f.act?.type ?? null;
  const hittable = !f.ko && t !== 'down' && t !== 'grabbed';
  return {
    x: f.x,
    y: f.y,
    actType: t,
    actT: f.act?.t ?? 0,
    blocking: f.blocking,
    blockHeld: f.blockHeld,
    hittable,
    grappleable: hittable && f.grappleImmune <= 0,
  };
}

function advanceAction(
  s: FightState,
  f: FighterState,
  self: Snapshot,
  opp: Snapshot,
  dt: number,
  pending: Pending[],
  dashing: [boolean, boolean],
): void {
  const a = f.act;
  if (!a) return;
  a.t += dt;
  const mods = modsOf(s, f.side);
  const oppMods = modsOf(s, f.side === 0 ? 1 : 0);
  const dx = opp.x - self.x;
  const dy = opp.y - self.y;
  const d = length(dx, dy) || 1;
  const ux = dx / d;
  const uy = dy / d;

  switch (a.type) {
    case 'strike':
    case 'strike2': {
      const tm = a.type === 'strike' ? strikeTiming(mods) : oneTwoTiming(mods);
      if (!a.done && a.t >= tm.hitStart && a.t <= tm.hitEnd) {
        const reach = reachOf(mods);
        if (d <= reach * oppMods.evadeMul) {
          a.done = true;
          pending.push({ kind: a.type, attacker: f.side });
        } else if (d <= reach && a.t + dt > tm.hitEnd) {
          // The window closes short of a target plain reach would have hit: Footwork dodged it (D49).
          a.done = true;
          pending.push({ kind: 'dodge', attacker: f.side });
        }
      }
      break;
    }
    case 'grapple': {
      const g = FIGHT.grapple;
      if (!a.done && a.t > g.dashStart && a.t < g.dashEnd) {
        const speed = g.dashSpeed * mods.takedownMul;
        f.vx = ux * speed;
        f.vy = uy * speed;
        dashing[f.side] = true;
        const contact = (mods.perks.longShot ? PERKS.longShot.contactRadii : g.contactRadii) * RADIUS;
        if (d < contact && opp.grappleable) {
          a.done = true;
          f.vx = f.vy = 0;
          pending.push({ kind: 'grapple', attacker: f.side });
        }
      }
      break;
    }
    case 'signature': {
      const tm = signatureTiming(mods);
      const sig = FIGHT.signature;
      if (!a.done && a.t > tm.windup && a.t < tm.dashEnd) {
        f.vx = ux * sig.dashSpeed;
        f.vy = uy * sig.dashSpeed;
        dashing[f.side] = true;
        if (d < sig.contactRadii * RADIUS) {
          a.done = true;
          f.vx = ux * sig.keepSpeed;
          f.vy = uy * sig.keepSpeed;
          pending.push({ kind: 'signature', attacker: f.side });
        }
      }
      break;
    }
    case 'grab':
      if (!a.done && a.t >= a.dur - 1e-9) {
        a.done = true;
        pending.push({ kind: 'slam', attacker: f.side });
      }
      break;
    default:
      break;
  }

  if (f.act === a && a.t >= a.dur - 1e-9) {
    if (a.type === 'down') endDown(s, f);
    else f.act = null;
  }
}

function resolve(
  s: FightState,
  p: Pending,
  snaps: [Snapshot, Snapshot],
  emit: Emit,
  clean: [boolean, boolean],
): void {
  const A = s.fighters[p.attacker];
  const T = s.fighters[p.attacker === 0 ? 1 : 0];
  const aMods = modsOf(s, A.side);
  const tMods = modsOf(s, T.side);
  const aSnap = snaps[A.side];
  const tSnap = snaps[T.side];
  const dx = tSnap.x - aSnap.x;
  const dy = tSnap.y - aSnap.y;
  const d = length(dx, dy) || 1;
  const ux = dx / d;
  const uy = dy / d;
  const r = s.round - 1;

  const gainMeter = (dmg: number) => {
    const points = dmg / 10;
    A.meter = Math.min(FIGHT.meterMax, A.meter + points * FIGHT.meterAttackerGain * aMods.meterGainMul);
    T.meter = Math.min(FIGHT.meterMax, T.meter + points * FIGHT.meterDefenderGain * tMods.meterGainMul);
    A.damageDealt[r] = (A.damageDealt[r] ?? 0) + dmg;
  };
  const impactX = T.x - ux * FIGHT.size * 0.3;
  const impactY = T.y - FIGHT.size * 0.5;
  const rockedMul = T.rocked > 0 ? FIGHT.rocked.damageTakenMul : 1;
  /** `side` won the exchange: open its punish window. */
  const openRead = (side: Side, kind: 'block' | 'counter') => {
    s.fighters[side].punishWindow = FIGHT.read.window;
    emit({ type: 'read', side, kind });
  };

  switch (p.kind) {
    case 'dodge': {
      if (tSnap.hittable) emit({ type: 'dodge', side: T.side });
      return;
    }

    case 'strike':
    case 'strike2': {
      if (!tSnap.hittable) return;
      const blocked = tSnap.blocking;
      const parried = blocked && tMods.perks.parry && tSnap.blockHeld <= PERKS.parry.window;
      const base = p.kind === 'strike' ? FIGHT.strike.damage : PERKS.oneTwo.damage;
      // Any strike that connects uses up the attacker's punish window; only a clean one pays off.
      const punish = !blocked && A.punishWindow > 0;
      A.punishWindow = 0;
      const raw =
        base * rockedMul * (blocked ? 1 - tMods.strikeBlockReduction : punish ? FIGHT.read.damageMul * aMods.readDamageMul : 1);
      const dmg = Math.round(raw * aMods.damageMul);
      A.powerDamage += Math.max(0, dmg - Math.round(raw));
      T.hp = Math.max(0, T.hp - dmg);
      let kb = p.kind === 'strike' ? FIGHT.strike.knockback : PERKS.oneTwo.knockback;
      if (blocked) kb *= FIGHT.defend.blockedKnockbackMul;
      else if (aMods.perks.heavyHands) kb *= PERKS.heavyHands.strikeKnockbackMul;
      if (punish) kb *= FIGHT.read.knockbackMul;
      T.kx += ux * kb;
      T.ky += uy * kb;
      T.hitFlash = 1;
      T.squash = blocked ? 0.5 : 1;
      if (blocked) {
        let cost: number = aMods.perks.guardCrusher
          ? PERKS.guardCrusher.blockedStrikeStaminaCost
          : FIGHT.blockedHitStaminaCost;
        if (parried) cost = 0;
        T.stamina = Math.max(0, T.stamina - cost);
        const bounce = FIGHT.defend.attackerBounce * (parried ? PERKS.parry.bounceMul : 1);
        A.kx -= ux * bounce;
        A.ky -= uy * bounce;
        if (aMods.perks.guardCrusher && cost > 0 && T.stamina <= 0) {
          setStun(T, PERKS.guardCrusher.guardBreakStun);
          emit({ type: 'guardBreak', side: T.side });
        } else {
          openRead(T.side, 'block');
        }
      } else {
        const stunBase = p.kind === 'strike' ? FIGHT.strike.stun : PERKS.oneTwo.stun;
        setStun(T, stunBase * (tMods.perks.tough ? PERKS.tough.strikeStunMul : 1));
        if (p.kind === 'strike' && aMods.perks.oneTwo) A.oneTwoWindow = PERKS.oneTwo.window;
        clean[T.side] = true;
      }
      gainMeter(dmg);
      emit({ type: 'hit', attacker: A.side, defender: T.side, move: p.kind, damage: dmg, blocked, parried, punish, x: impactX, y: impactY });
      return;
    }

    case 'signature': {
      if (!tSnap.hittable) return;
      const blocked = tSnap.blocking;
      const reduction = blocked
        ? tMods.perks.ironGuard
          ? PERKS.ironGuard.signatureReduction
          : FIGHT.defend.signatureReduction
        : 0;
      const raw = FIGHT.signature.damage * rockedMul * (1 - reduction);
      const dmg = Math.round(raw * aMods.damageMul);
      A.powerDamage += Math.max(0, dmg - Math.round(raw));
      T.hp = Math.max(0, T.hp - dmg);
      let kb: number = FIGHT.signature.knockback;
      if (blocked) kb *= tMods.perks.ironGuard ? 0 : FIGHT.defend.blockedKnockbackMul;
      T.kx += ux * kb;
      T.ky += uy * kb;
      T.hitFlash = 1;
      T.squash = blocked ? 0.5 : 1;
      A.punishWindow = 0;
      if (blocked) {
        T.stamina = Math.max(0, T.stamina - FIGHT.blockedHitStaminaCost);
        A.kx -= ux * FIGHT.defend.attackerBounce;
        A.ky -= uy * FIGHT.defend.attackerBounce;
        openRead(T.side, 'block');
      } else {
        setStun(T, FIGHT.signature.stun);
        s.timeScale = Math.min(s.timeScale, FIGHT.slowMo.signature);
        clean[T.side] = true;
      }
      gainMeter(dmg);
      emit({ type: 'hit', attacker: A.side, defender: T.side, move: 'signature', damage: dmg, blocked, parried: false, punish: false, x: impactX, y: impactY });
      return;
    }

    case 'grapple': {
      if (!tSnap.grappleable) return;
      const counterWindow = tMods.perks.stopHit
        ? strikeTiming(tMods).duration
        : FIGHT.grapple.counterWindow;
      const striking = tSnap.actType === 'strike' || tSnap.actType === 'strike2';
      A.punishWindow = 0;
      if (striking && tSnap.actT < counterWindow) {
        setStun(A, FIGHT.grapple.counterStun);
        A.vx = A.vy = 0;
        emit({ type: 'counter', striker: T.side, grappler: A.side });
        openRead(T.side, 'counter');
        return;
      }
      // A hit landing on the grappler in this same step interrupts the grab.
      if (A.act?.type !== 'grapple') return;
      A.grabRead = tSnap.blocking;
      startAct(A, 'grab', FIGHT.grapple.grabFreeze);
      startAct(T, 'grabbed', FIGHT.grapple.grabFreeze);
      for (const f of [A, T]) f.vx = f.vy = f.kx = f.ky = 0;
      emit({ type: 'grab', attacker: A.side, defender: T.side });
      return;
    }

    case 'slam': {
      if (T.ko) return;
      const fenceSlam =
        aMods.perks.fenceSlam && fenceDistance(s, T) <= PERKS.fenceSlam.fenceDistanceRadii * RADIUS;
      const punish = A.grabRead;
      A.grabRead = false;
      const raw =
        FIGHT.grapple.damage *
        rockedMul *
        (fenceSlam ? PERKS.fenceSlam.damageMul : 1) *
        (punish ? FIGHT.read.damageMul : 1);
      // Escape softens the slam (D49); the attacker's Takedown adds to what lands.
      const taken = raw * tMods.takedownTakenMul;
      const dmg = Math.round(taken * aMods.takedownMul);
      A.takedownDamage += Math.max(0, dmg - Math.round(taken));
      T.hp = Math.max(0, T.hp - dmg);
      T.kx += ux * FIGHT.grapple.knockback;
      T.ky += uy * FIGHT.grapple.knockback;
      T.hitFlash = 1;
      T.squash = 1;
      startAct(T, 'down', FIGHT.grapple.down * tMods.downMul);
      clean[T.side] = true;
      gainMeter(dmg);
      emit({ type: 'takedown', attacker: A.side, defender: T.side, damage: dmg, fenceSlam, punish, x: T.x, y: T.y - FIGHT.size * 0.3 });
      return;
    }
  }
}

function checkKnockouts(s: FightState, emit: Emit): void {
  const down: Side[] = [];
  for (const f of s.fighters) {
    if (f.ko || f.hp > 0) continue;
    if (modsOf(s, f.side).perks.ironChin && !f.ironChinUsed) {
      f.ironChinUsed = true;
      f.hp = PERKS.ironChin.leaveHealth;
      emit({ type: 'ironChin', side: f.side });
      continue;
    }
    f.ko = true;
    f.koT = 0;
    f.act = null;
    f.queued = null;
    f.blocking = false;
    f.rocked = 0;
    f.punishWindow = 0;
    f.kx *= FIGHT.koKnockbackMul;
    f.ky *= FIGHT.koKnockbackMul;
    down.push(f.side);
  }
  if (down.length === 0 || s.phase !== 'fight') return;
  const winner: Side | null = down.length === 2 ? null : down[0] === 0 ? 1 : 0;
  const loser = down[0] as Side;
  s.timeScale = Math.min(s.timeScale, FIGHT.slowMo.ko);
  const result: FightResult = {
    winner,
    method: winner === null ? 'draw' : 'ko',
    round: s.round,
    roundTime: FIGHT.roundSeconds - s.clock,
    roundWinners: [...s.roundWinners],
    damage: totalDamage(s),
    skillDamage: skillDamageOf(s),
  };
  result.roundWinners[s.round - 1] = winner;
  s.roundWinners = [...result.roundWinners];
  s.result = result;
  emit({ type: 'ko', loser, winner });
  enterPhase(s, 'ko');
}

/**
 * A clean hit that leaves a fighter low (Iron chin's save included) rocks them, once per round.
 * Runs after the knockout check, so a KO never also rocks.
 */
function checkRocked(s: FightState, emit: Emit, clean: [boolean, boolean]): void {
  if (s.phase !== 'fight') return;
  for (const f of s.fighters) {
    if (!clean[f.side] || f.ko || f.rockedThisRound) continue;
    if (f.hp > f.maxHp * FIGHT.rocked.healthShare) continue;
    f.rocked = FIGHT.rocked.duration;
    f.rockedThisRound = true;
    f.punishWindow = 0;
    f.queued = null;
    emit({ type: 'rocked', side: f.side });
  }
}

// ---------------------------------------------------------------------------------------------
// Movement and space

function move(s: FightState, f: FighterState, input: FighterInput, dt: number, dashing: boolean): void {
  const a = f.act;
  if (f.ko) {
    f.x += (f.vx + f.kx) * dt;
    f.y += (f.vy + f.ky) * dt;
    return;
  }
  if (a?.type === 'grab' || a?.type === 'grabbed') {
    f.vx = f.vy = f.kx = f.ky = 0;
    return;
  }
  if (!dashing) {
    const mods = modsOf(s, f.side);
    const locked = isLocked(a);
    const ix = locked ? 0 : input.mx;
    const iy = locked ? 0 : input.my;
    let speed = FIGHT.moveSpeed * mods.speedMul;
    if (f.rocked > 0) speed *= FIGHT.rocked.speedMul;
    if (f.blocking) speed *= mods.perks.lightFeet ? PERKS.lightFeet.blockSpeedMul : FIGHT.blockSpeedMul;
    if (a && !locked) {
      const striking = a.type === 'strike' || a.type === 'strike2';
      speed *= striking && mods.perks.jabAndMove ? PERKS.jabAndMove.attackSpeedMul : FIGHT.attackSpeedMul;
    }
    if (mods.perks.cageEscape && fenceDistance(s, f) <= PERKS.cageEscape.fenceDistanceRadii * RADIUS) {
      speed *= PERKS.cageEscape.speedMul;
    }
    const k = Math.min(1, dt * FIGHT.accel);
    f.vx += (ix * speed - f.vx) * k;
    f.vy += (iy * speed - f.vy) * k;
  }
  f.x += (f.vx + f.kx) * dt;
  f.y += (f.vy + f.ky) * dt;
}

function separate(s: FightState): void {
  const [a, b] = s.fighters;
  if (a.ko || b.ko) return;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = length(dx, dy) || 1;
  const min = RADIUS * FIGHT.pushApartRadii;
  if (d < min) {
    const o = (min - d) / 2;
    const ux = dx / d;
    const uy = dy / d;
    a.x -= ux * o;
    a.y -= uy * o;
    b.x += ux * o;
    b.y += uy * o;
  }
}

function constrain(s: FightState, f: FighterState): void {
  const [cx, cy] = s.setup.arena.center;
  const inset = RADIUS * FIGHT.fenceInsetRadii;
  const bounce = 1 + FIGHT.fenceRestitution;
  for (let k = 0; k < 8; k++) {
    const [nx, ny] = OCTAGON_NORMALS[k]!;
    const lim = (s.setup.arena.apothems[k] ?? 0) - inset;
    const p = (f.x - cx) * nx + (f.y - cy) * ny;
    if (p > lim) {
      f.x -= nx * (p - lim);
      f.y -= ny * (p - lim);
      const vn = f.vx * nx + f.vy * ny;
      if (vn > 0) {
        f.vx -= nx * vn;
        f.vy -= ny * vn;
      }
      const kn = f.kx * nx + f.ky * ny;
      if (kn > 0) {
        f.kx -= nx * kn * bounce;
        f.ky -= ny * kn * bounce;
      }
    }
  }
}

/** Distance from a fighter to the closest point it can reach at the fence. */
export function fenceDistance(s: FightState, f: FighterState): number {
  const [cx, cy] = s.setup.arena.center;
  const inset = RADIUS * FIGHT.fenceInsetRadii;
  let best = Infinity;
  for (let k = 0; k < 8; k++) {
    const [nx, ny] = OCTAGON_NORMALS[k]!;
    const lim = (s.setup.arena.apothems[k] ?? 0) - inset;
    const gap = lim - ((f.x - cx) * nx + (f.y - cy) * ny);
    if (gap < best) best = gap;
  }
  return best;
}
